import React, { useLayoutEffect, useRef, useState } from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { parseInlineBold } from '../primitives/InlineBold'
import { EditableText } from '../slides/EditableText'
import type { EditableSlideProps } from '../slides/contracts'
import type { ExecSummarySlideData } from '../slides/renderer-internal-types'

/**
 * The scr-cards slot (plots plan Stage 2, AA): the exec summary's story columns, one
 * card each (`repeat(n, 1fr)`, gap 24), a section title over the column's bullets
 * (or a legacy paragraph). The bullets share the card body's measured height by
 * need (`allocateBulletLines`). The three cards step the same brand wash from
 * strongest to lightest. A leading "1. " on a stored label is the old step
 * chip; the title shows without it.
 */
const SCR_CARD_BG: Array<string> = [
  'var(--ds-exec-scr-card-a)',
  'var(--ds-exec-scr-card-b)',
  'var(--ds-exec-scr-card-c)',
]

function sectionTitle(raw: string): string {
  const t = raw.trim()
  const m = t.match(/^\d+\.\s*(.+)$/)
  return m ? m[1] : t
}

/** Vertical rhythm of the bullet stack: `--ds-gap-sm` between rows, plus a descender's slack. */
const SCR_BULLET_GAP_PX = 8
const SCR_BULLET_SLACK_PX = 3

/**
 * Share a column's line budget between its bullets by need rather than evenly.
 *
 * Every bullet keeps the lines it actually wants while the budget lasts — which is the
 * usual case, so nothing is clamped at all. Only once the column is over budget does the
 * greediest bullet give a line back, one at a time, so the shortfall lands on the bullet
 * with the most to spare instead of on all of them equally. An even split would clamp a
 * five-line bullet next to a three-line one while a third of the card sat empty.
 */
export function allocateBulletLines(
  naturalLines: Array<number>,
  availableLines: number,
): Array<number> {
  const allocated = naturalLines.map((lines) => Math.max(1, lines))
  let total = allocated.reduce((sum, lines) => sum + lines, 0)

  while (total > availableLines) {
    let greediest = 0
    for (let i = 1; i < allocated.length; i += 1) {
      if (allocated[i] > allocated[greediest]) {
        greediest = i
      }
    }
    if (allocated[greediest] <= 1) {
      break
    }
    allocated[greediest] -= 1
    total -= 1
  }

  return allocated
}

/**
 * Lines each bullet in one card body may use, or `undefined` when the card cannot be
 * measured yet.
 *
 * Overflow policy §5 (composed UI): the bullets share one `flex: 1` slot, and the slot's
 * measured height is the only bound. No bullet has a cap of its own and there is no even
 * split: each keeps the lines its text needs, and lines are given back only when the
 * bullets together need more than the slot holds. The slot's height is not a constant —
 * it loses a line whenever the section label above it wraps — so it is measured rather
 * than assumed, and a long title costs the bullets exactly the line it took.
 */
function measureBulletLines(slot: HTMLElement): Array<number> | undefined {
  const markers = Array.from(slot.querySelectorAll<HTMLElement>('[data-scr-bullet-marker]'))
  if (markers.length === 0 || slot.clientHeight <= 0) {
    return undefined
  }

  const lineHeightPx = parseFloat(getComputedStyle(markers[0]).lineHeight)
  if (!Number.isFinite(lineHeightPx) || lineHeightPx <= 0) {
    return undefined
  }

  const usablePx =
    slot.clientHeight - SCR_BULLET_SLACK_PX - SCR_BULLET_GAP_PX * (markers.length - 1)
  const availableLines = Math.max(markers.length, Math.floor(usablePx / lineHeightPx))

  const naturalLines = markers.map((marker) => {
    const text = marker.parentElement?.children[1] as HTMLElement | undefined
    if (!text) {
      return 1
    }
    // `-webkit-line-clamp` does not reliably report the lines it hid through
    // `scrollHeight`, so lift the clamp for the read and restore it. This runs inside a
    // layout effect, before paint, so the lifted state is never visible.
    //
    // Lifting is also what makes the reading font-independent. `scrollHeight`
    // carries glyph overflow, so a face whose ascent+descent exceed the line box
    // reports ~1.7x the height it occupies (measured on the deck's display
    // face; the body face used here reports 1.0). Because the clamp is lifted
    // and the count is taken from the same element, that factor divides out.
    // Comparing a raw `scrollHeight` against a clamped `clientHeight` does not
    // divide out, which is the bug fixed in FitText and TextClamp.
    const clamp = text.style.webkitLineClamp
    const maxHeight = text.style.maxHeight
    text.style.webkitLineClamp = 'unset'
    text.style.maxHeight = 'none'
    const lines = Math.round(text.scrollHeight / lineHeightPx)
    text.style.webkitLineClamp = clamp
    text.style.maxHeight = maxHeight
    return Math.max(1, lines)
  })

  return allocateBulletLines(naturalLines, availableLines)
}

function sameAllocations(a: Array<Array<number> | undefined>, b: Array<Array<number> | undefined>): boolean {
  return (
    a.length === b.length
    && a.every((lines, i) => {
      const other = b[i]
      if (!lines || !other) {
        return lines === other
      }
      return lines.length === other.length && lines.every((value, j) => value === other[j])
    })
  )
}

const EXEC_SUMMARY = 'exec-summary' as const

export interface SlotScrCardsProps extends EditableSlideProps {
  columns: NonNullable<ExecSummarySlideData['columns']>
}

export function SlotScrCards(props: SlotScrCardsProps) {
  const { columns, editable, onEdit, renderMode } = props
  const colCount = Math.max(columns.length, 1)
  const scrNarrativeClamp = schemaBackedProseClampProps('scrColumnNarrative', EXEC_SUMMARY, {
    lineHeight: '1.6em',
  })
  const scrBulletClamp = schemaBackedProseClampProps('scrColumnBullet', EXEC_SUMMARY, {
    lineHeight: '1.6em',
  })
  const sectionTitleClamp = schemaBackedProseClampProps('sectionLabel', EXEC_SUMMARY, {
    lineHeight: '1.4em',
  })
  // Measure each card body after layout so the bullets share the space the section label
  // actually left behind. `ResizeObserver` covers later reflows (font swap, a label
  // rewritten in edit mode). Before the first measure (jsdom, which has no box metrics)
  // no bullet is clamped: the body's own box bounds them, never an even split.
  const bodyRefs = useRef<Array<HTMLDivElement | null>>([])
  const [bulletAllocations, setBulletAllocations] = useState<Array<Array<number> | undefined>>([])

  useLayoutEffect(() => {
    const slots = bodyRefs.current.slice(0, columns.length)
    if (slots.length === 0) {
      return
    }

    const measure = () => {
      const next = slots.map((slot) => (slot ? measureBulletLines(slot) : undefined))
      setBulletAllocations((current) => (sameAllocations(current, next) ? current : next))
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    slots.forEach((slot) => {
      if (slot) {
        observer.observe(slot)
      }
    })
    return () => observer.disconnect()
  }, [columns])

  // SCR row: three equal columns; height is flex-derived so cards stay aligned (Figma layout_9ZTV77).
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        gap: 'var(--ds-gap-xl)',
        alignItems: 'stretch',
      }}
    >
      {columns.map((col, idx) => {
        const title = sectionTitle(col.sectionLabel)
        // The writer's typed points are the bullets, rendered as written. A
        // legacy paragraph (`narrative`) is never split into bullets: splitting
        // at punctuation cut names such as "Real Food. Real Results".
        const bodyBullets = Array.isArray(col.points)
          ? col.points.filter(
              (item): item is string => typeof item === 'string' && item.trim().length > 0,
            )
          : []
        const allocatedBulletLines = bulletAllocations[idx]
        const narrative =
          typeof col.narrative === 'string'
            ? parseInlineBold(col.narrative, {
                color: 'var(--ds-exec-scr-body)',
                fontWeight: 600,
              })
            : col.narrative

        return (
          <div
            key={idx}
            style={{
              // A static render grows the card around its whole text (wave D).
              ...(renderMode === 'static' ? { minHeight: '100%', alignSelf: 'start' } : { height: '100%', minHeight: 0 }),
              display: 'flex',
              flexDirection: 'column',
              background: SCR_CARD_BG[idx % SCR_CARD_BG.length],
              borderRadius: 'var(--ds-radius-card)',
              padding: '24px 20px',
              gap: 'var(--ds-gap-sm)',
              // A static render draws every bullet whole (wave D): text past the card
              // shows, and the pipeline already recorded it as over its box.
              overflow: renderMode === 'static' ? 'visible' : 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--ds-gap-md)',
                flexShrink: 0,
              }}
            >
              {editable && onEdit ? (
                <EditableText
                  value={title}
                  field={`columns.${idx}.sectionLabel`}
                  editable
                  onEdit={onEdit}
                  tag="span"
                  style={{
                    fontFamily: 'var(--ds-font-sans)',
                    fontSize: 'var(--ds-fs-exec-card-title)',
                    fontWeight: 'var(--ds-weight-medium)',
                    color: 'var(--ds-text-primary)',
                  }}
                />
              ) : (
                <TextClamp
                  renderMode={renderMode}
                  as="span"
                  minLines={sectionTitleClamp.minLines}
                  maxLines={sectionTitleClamp.maxLines}
                  lineHeight={sectionTitleClamp.lineHeight}
                  style={{
                    fontFamily: 'var(--ds-font-sans)',
                    fontSize: 'var(--ds-fs-exec-card-title)',
                    fontWeight: 'var(--ds-weight-medium)',
                    color: 'var(--ds-text-primary)',
                  }}
                >
                  {title}
                </TextClamp>
              )}
            </div>
            <div
              ref={(node) => {
                bodyRefs.current[idx] = node
              }}
              data-testid={`exec-summary-column-body-${idx}`}
              style={{ flex: 1, minHeight: 0, overflow: renderMode === 'static' ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column', gap: 'var(--ds-gap-sm)' }}
            >
              {bodyBullets.length > 0 ? (
                <div
                  data-testid={`exec-summary-column-bullets-${idx}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--ds-gap-sm)',
                  }}
                >
                  {bodyBullets.map((bullet, bulletIdx) => {
                    const bulletContent = parseInlineBold(bullet, {
                      color: 'var(--ds-exec-scr-body)',
                      fontWeight: 600,
                    })

                    return (
                      <div
                        key={`${idx}-${bulletIdx}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '12px minmax(0, 1fr)',
                          columnGap: 'var(--ds-gap-sm)',
                          alignItems: 'start',
                        }}
                      >
                        {/* Carries the bullet line box: the layout effect reads its
                            computed line-height to size the shared budget. */}
                        <span
                          aria-hidden="true"
                          data-scr-bullet-marker=""
                          style={{
                            color: 'var(--ds-exec-scr-body)',
                            fontSize: 'var(--ds-fs-body-lg)',
                            lineHeight: '1.6em',
                          }}
                        >
                          •
                        </span>
                        {editable && onEdit ? (
                          <EditableText
                            value={bullet}
                            field={`columns.${idx}.points.${bulletIdx}`}
                            editable
                            onEdit={onEdit}
                            tag="span"
                            style={{
                              fontFamily: 'var(--ds-font-sans)',
                              fontSize: 'var(--ds-fs-body-lg)',
                              letterSpacing: '-0.02em',
                              color: 'var(--ds-exec-scr-body)',
                            }}
                          />
                        ) : (
                          <TextClamp
                            renderMode={renderMode}
                            minLines={scrBulletClamp.minLines}
                            maxLines={allocatedBulletLines?.[bulletIdx]}
                            lineHeight={scrBulletClamp.lineHeight}
                            style={{
                              fontFamily: 'var(--ds-font-sans)',
                              fontSize: 'var(--ds-fs-body-lg)',
                              letterSpacing: '-0.02em',
                              color: 'var(--ds-exec-scr-body)',
                            }}
                          >
                            {bulletContent}
                          </TextClamp>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : editable && onEdit ? (
                <EditableText
                  value={typeof col.narrative === 'string' ? col.narrative : ''}
                  field={`columns.${idx}.narrative`}
                  editable
                  onEdit={onEdit}
                  tag="p"
                  multiline
                  style={{
                    fontFamily: 'var(--ds-font-sans)',
                    fontSize: 'var(--ds-fs-body-lg)',
                    letterSpacing: '-0.02em',
                    color: 'var(--ds-exec-scr-body)',
                  }}
                />
              ) : (
                <TextClamp
                  renderMode={renderMode}
                  minLines={scrNarrativeClamp.minLines}
                  maxLines={scrNarrativeClamp.maxLines}
                  lineHeight={scrNarrativeClamp.lineHeight}
                  style={{
                    fontFamily: 'var(--ds-font-sans)',
                    fontSize: 'var(--ds-fs-body-lg)',
                    letterSpacing: '-0.02em',
                    color: 'var(--ds-exec-scr-body)',
                  }}
                >
                  {narrative}
                </TextClamp>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
